import { GetServerSideProps } from 'next';
import { useRouter } from 'next/router';
import operationSignatures from '../_constants/operationSignatures.json';
import { getProcessEnvs, PageRendererComponent, prePageCall, processAcceptLanguage } from '@marriott/mi-headless-utils';
import { FEATURE_FLAGS, FeatureFlagService, TARGETS } from '@marriott/mi-feature-flag-utils';
import { DataLayerWrapper, NEXT_PUBLIC_ENV_KEYS } from '@marriott/mi-groups-components';
import { brandSpecificUrls } from '../renderer.config';

function PageRenderer(props) {
  const router = useRouter();
  return (
    <DataLayerWrapper {...props}>
      <PageRendererComponent {...props} key={router.asPath} />
    </DataLayerWrapper>
  );
}

//Any product specific page data apart from common data like session, datalayer
//can be returned from this function and the same is passed as props to the page Component
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const groupsSpecificData = async (context: any) => {
  const { GOOGLE_MAP_API_KEY } = getProcessEnvs();
  // to get the dtt flag from Harness FeatureFlagService
  const featureFlagApiKey = process.env['FEATURE_FLAG_API_KEY_SERVER'];
  const featureFlagService = new FeatureFlagService(featureFlagApiKey || '');
  const locale = processAcceptLanguage(context?.req?.headers?.['accept-language'] ?? '');
  const dttFlags: { [key in FEATURE_FLAGS]?: string | boolean | Record<string, unknown> } =
    await featureFlagService.getAllFlags({
      target: {
        ...TARGETS.APP.GROUPS_RENDERER,
        identifier: `${TARGETS.APP.GROUPS_RENDERER.identifier}-${locale}`,
        name: `${TARGETS.APP.GROUPS_RENDERER.name}-${locale}`,
      },
    });

  // Fetch branch IO app download flag with per-user target for A/B testing
  const sessionId = context?.req?.cookies?.sessionID || '';
  if (sessionId) {
    try {
      const sessionTargetedFlags = await featureFlagService.getFlags(
        [
          FEATURE_FLAGS.EXPERIENCE_MCOM_GROUPS_BRANCH_IO_APP_DOWNLOAD_ENABLED,
          FEATURE_FLAGS.EXPERIENCE_MCOM_DTT_SWP_GROUPS,
        ],
        {
          target: {
            ...TARGETS.APP.GROUPS_RENDERER,
            identifier: `${TARGETS.APP.GROUPS_RENDERER.identifier}-${sessionId}`,
            name: sessionId,
          },
        }
      );
      dttFlags[FEATURE_FLAGS.EXPERIENCE_MCOM_GROUPS_BRANCH_IO_APP_DOWNLOAD_ENABLED] =
        sessionTargetedFlags[FEATURE_FLAGS.EXPERIENCE_MCOM_GROUPS_BRANCH_IO_APP_DOWNLOAD_ENABLED];
      dttFlags[FEATURE_FLAGS.EXPERIENCE_MCOM_DTT_SWP_GROUPS] =
        sessionTargetedFlags[FEATURE_FLAGS.EXPERIENCE_MCOM_DTT_SWP_GROUPS];
    } catch (error) {
      console.error('Error fetching branch IO app download flag:', error);
    }
  }

  const isDttEnabledFromHarness = dttFlags[FEATURE_FLAGS.EXPERIENCE_MCOM_DTT_GROUP];

  if (isDttEnabledFromHarness) {
    context?.res?.setHeader('Set-Cookie', `dtt=${isDttEnabledFromHarness}; Path=/; Secure; SameSite=None`);
  }

  // TODO: Temporary - read isSwp mod header for testing; to be removed once no longer needed
  const isSwpFromHeader = context?.req?.headers?.['swp'] === 'true';

  return {
    operationSignatures,
    nextPublicEnvKeys: NEXT_PUBLIC_ENV_KEYS,
    selector: brandSpecificUrls.includes(context.query.page.join('/')) ? context.query.brandCode : null,
    dttFlags,
    isSwpFromHeader,
    GOOGLE_MAP_API_KEY,
  };
};

//Make pre page calls like session, datalayer, fetch model.json, producSpecifc data etc
//at server side and pass the data is passed as props to page component.
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export const getServerSideProps: GetServerSideProps<any> = prePageCall(groupsSpecificData);
export default PageRenderer;
